import { useEffect, useRef, useState } from 'react';
import { appConfig } from '@/config/app';
import { initGoogleSignIn, type GoogleSignInHandle } from '@/integrations/googleIdentity';
import { useT } from '@/i18n/LocaleProvider';
import styles from './AuthScreen.module.css';

interface Props {
  /** Текст официальной кнопки под вкладку: continue_with / signup_with. */
  text: 'continue_with' | 'signup_with';
  onCredential: (idToken: string) => void;
  onUnavailable: () => void;
  disabled?: boolean;
}

/** Виден ли вход через Google: только при заданном client id (AUTH-5); в mock-режиме — имитация. */
export function isGoogleSignInEnabled(): boolean {
  return !!appConfig.googleClientId || appConfig.apiMode === 'mock';
}

/**
 * «Continue with Google» по макету. Реальный вход даёт официальная кнопка GIS,
 * отрисованная невидимой поверх (клик достаётся ей); One Tap показывается при
 * открытии экрана; с клавиатуры кнопка макета вызывает prompt().
 */
export function GoogleButton({ text, onCredential, onUnavailable, disabled }: Props) {
  const t = useT();
  const overlayRef = useRef<HTMLDivElement>(null);
  const handleRef = useRef<GoogleSignInHandle | null>(null);
  const onCredentialRef = useRef(onCredential);
  onCredentialRef.current = onCredential;
  const onUnavailableRef = useRef(onUnavailable);
  onUnavailableRef.current = onUnavailable;
  const [ready, setReady] = useState(false);
  const clientId = appConfig.googleClientId;
  const isMock = !clientId && appConfig.apiMode === 'mock';

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    initGoogleSignIn(clientId, (idToken) => onCredentialRef.current(idToken))
      .then((handle) => {
        if (cancelled) return;
        handleRef.current = handle;
        setReady(true);
        handle.prompt();
      })
      .catch(() => {
        if (!cancelled) setReady(false);
      });
    return () => {
      cancelled = true;
      handleRef.current?.cancel();
    };
  }, [clientId]);

  useEffect(() => {
    const el = overlayRef.current;
    const handle = handleRef.current;
    if (!ready || !el || !handle) return;
    el.innerHTML = '';
    handle.renderButton(el, el.clientWidth || 352, text);
  }, [ready, text]);

  const onVisualClick = () => {
    if (isMock) {
      onCredentialRef.current('mock-google-id-token');
      return;
    }
    if (handleRef.current) handleRef.current.prompt();
    else onUnavailableRef.current();
  };

  const overlayActive = !!clientId && ready;

  return (
    <div className={styles.googleWrap}>
      {clientId ? (
        <div ref={overlayRef} className={styles.googleOverlay} aria-hidden={!ready} />
      ) : null}
      <button
        type="button"
        className={styles.google}
        onClick={onVisualClick}
        disabled={disabled}
        tabIndex={overlayActive ? -1 : 0}
        aria-hidden={overlayActive || undefined}
      >
        <GoogleGlyph />
        {t('auth.google')}
      </button>
    </div>
  );
}

/** Официальный знак «G» Google (4 фирменных цвета) — знак третьей стороны, не токены дизайн-системы. */
function GoogleGlyph() {
  return (
    <svg className={styles.googleIcon} viewBox="0 0 48 48" aria-hidden="true" focusable="false">
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}
